import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Table, Space, Popconfirm, message, Select, Card, Typography, Row, Col, Statistic, InputNumber, Tag, Modal } from 'antd';
import { SearchOutlined, AppstoreAddOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ShoppingCartOutlined, DollarOutlined, InboxOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/constants';

const Product = () => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const [searchText, setSearchText] = useState('');
  const [productSales, setProductSales] = useState([]); // new state
  const [selectedProduct, setSelectedProduct] = useState(null); // new state

  const units = ['NOS', 'LTR', 'CM', 'MM', 'KG'];

  const fetchProducts = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_PRODUCT);
      if (response.data.success && Array.isArray(response.data.data)) {
        setProducts(response.data.data);
        setFilteredProducts(response.data.data);
      } else {
        throw new Error('Data format is incorrect');
      }
    } catch (error) {
      message.error('Failed to fetch products.');
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value) {
      setFilteredProducts(products);
      return;
    }

    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const handleFinish = async (values) => {
    try {
      const productExists = products.some(
        (product) =>
          product.name.toLowerCase() === values.name.toLowerCase() &&
          product.id !== editingKey
      );

      if (productExists) {
        message.error('Product already exists with this name!');
        return;
      }

      if (editingKey) {
        await axios.post(API_ENDPOINTS.UPDATE_PRODUCT, { ...values, id: editingKey });
        message.success('Product updated successfully!');
      } else {
        await axios.post(API_ENDPOINTS.ADD_PRODUCT, values);
        message.success('Product added successfully!');
      }

      form.resetFields();
      setEditingKey('');
      fetchProducts();
      setSearchText('');
    } catch (error) {
      message.error('Failed to save product.');
    }
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      ...record,
      unit: record.unit || 'NOS',
    });
    setEditingKey(record.id);
  };

  const handleDelete = async (id) => {
    try {
      await axios.post(API_ENDPOINTS.DELETE_PRODUCT, { id });
      message.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      message.error('Failed to delete product.');
    }
  };

  // 🔹 New: Fetch product sales history
  const handleShowDetails = async (record) => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.GET_PRODUCT_SALES}?product_name=${encodeURIComponent(record.name)}`);
      if (response.data.success) {
        setSelectedProduct(record.name);
        setProductSales(response.data.data);
      } else {
        setSelectedProduct(null);
        setProductSales([]);
        message.info('No sales found for this product.');
      }
    } catch (error) {
      message.error('Failed to fetch product sales.');
    }
  };

  const columns = [
    {
      title: '#',
      width: 50,
      render: (_, __, index) => (
        <span style={{ fontWeight: 500, color: '#666' }}>{index + 1}</span>
      ),
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => <Tag color="blue">₹{parseFloat(price).toFixed(2)}</Tag>,
    },
    {
      title: 'Stock',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity) => (
        <Tag color={quantity < 10 ? 'red' : quantity < 50 ? 'orange' : 'green'}>
          {quantity}
        </Tag>
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      render: (unit) => <Tag>{unit}</Tag>,
    },
    {
      title: 'Value',
      key: 'value',
      render: (_, record) => (
        <Tag color="purple">₹{(record.price * record.quantity).toFixed(2)}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={editingKey !== ''}
          >
            Edit
          </Button>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleShowDetails(record)}
          >
            Details
          </Button>
          <Popconfirm
            title="Delete Product"
            description="Are you sure you want to delete this product?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const totalValue = products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
  const lowStockProducts = products.filter(product => product.quantity < 10).length;

  return (
    <div className="fade-in">
          <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
                  <div>
                    <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                      TOTAL CATALOG PRODUCTS
                    </Typography.Text>
                    <Statistic
                      value={products.length}
                      valueStyle={{ color: '#0F172A', fontWeight: 800, fontSize: '24px' }}
                    />
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: '#EEF2FF', color: '#4F46E5' }}>
                    <ShoppingCartOutlined />
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
                  <div>
                    <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                      INVENTORY VALUATION
                    </Typography.Text>
                    <Statistic
                      value={totalValue}
                      precision={2}
                      prefix="₹"
                      valueStyle={{ color: '#10B981', fontWeight: 800, fontSize: '24px' }}
                    />
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: '#ECFDF5', color: '#10B981' }}>
                    <DollarOutlined />
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
                  <div>
                    <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                      LOW STOCK ALERT (&le; 10)
                    </Typography.Text>
                    <Statistic
                      value={lowStockProducts}
                      valueStyle={{ color: lowStockProducts > 0 ? '#EF4444' : '#10B981', fontWeight: 800, fontSize: '24px' }}
                    />
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: lowStockProducts > 0 ? '#FEF2F2' : '#ECFDF5', color: lowStockProducts > 0 ? '#EF4444' : '#10B981' }}>
                    <InboxOutlined />
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
                  <div>
                    <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                      MATCHED PRODUCTS
                    </Typography.Text>
                    <Statistic
                      value={filteredProducts.length}
                      valueStyle={{ color: '#8B5CF6', fontWeight: 800, fontSize: '24px' }}
                    />
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: '#F3E8FF', color: '#8B5CF6' }}>
                    <SearchOutlined />
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={9}>
              <Card
                title={
                  <Space size="middle">
                    <AppstoreAddOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>
                      {editingKey ? "Edit Product Details" : "Add Product to Inventory"}
                    </span>
                  </Space>
                }
                style={{ borderRadius: '16px' }}
              >
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  initialValues={{ unit: 'NOS' }}
                >
                  <Form.Item
                    name="name"
                    label="Product Name"
                    rules={[{ required: true, message: 'Please enter the product name' }]}
                  >
                    <Input
                      placeholder="e.g. Banner Printing (Vinyl)"
                      prefix={<ShoppingCartOutlined style={{ color: '#94A3B8' }} />}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item
                    name="price"
                    label="Unit Selling Price (₹)"
                    rules={[{ required: true, message: 'Please enter the price' }]}
                  >
                    <InputNumber
                      placeholder="0.00"
                      min={0}
                      step={0.01}
                      style={{ width: '100%' }}
                      size="large"
                      prefix="₹"
                    />
                  </Form.Item>
                  <Form.Item
                    name="quantity"
                    label="Available Stock Quantity"
                    rules={[{ required: true, message: 'Please enter the stock quantity' }]}
                  >
                    <InputNumber
                      placeholder="0"
                      min={0}
                      style={{ width: '100%' }}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item
                    name="unit"
                    label="Unit of Measurement"
                    rules={[{ required: true, message: 'Please select a unit' }]}
                  >
                    <Select placeholder="Select measurement unit" size="large">
                      {units.map(unit => (
                        <Select.Option key={unit} value={unit}>
                          {unit}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 0, marginTop: '20px' }}>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                      {editingKey && (
                        <Button
                          onClick={() => {
                            form.resetFields();
                            setEditingKey('');
                          }}
                          size="large"
                          style={{ borderRadius: '10px' }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={editingKey ? <EditOutlined /> : <AppstoreAddOutlined />}
                        size="large"
                        style={{ borderRadius: '10px' }}
                      >
                        {editingKey ? "Update Product" : "Save Product"}
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            <Col xs={24} lg={15}>
              <Card
                title={
                  <Space size="middle">
                    <ShoppingCartOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Product Catalog</span>
                  </Space>
                }
                extra={
                  <Input.Search
                    placeholder="Search products..."
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ width: 260 }}
                    allowClear
                    size="large"
                  />
                }
                style={{ borderRadius: '16px' }}
              >
                <Table
                  columns={columns}
                  dataSource={filteredProducts}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`
                  }}
                  scroll={{ x: 750 }}
                  size="middle"
                />
              </Card>
            </Col>
          </Row>

          <Modal
            title={`Sales History - ${selectedProduct}`}
            open={!!selectedProduct}
            onCancel={() => {
              setSelectedProduct(null);
              setProductSales([]);
            }}
            footer={null}
            width={750}
            style={{ borderRadius: '16px' }}
          >
            <Table
              columns={[
                { title: 'Invoice No', dataIndex: 'invoice_number', key: 'invoice_number', render: text => <strong>{text}</strong> },
                { title: 'Customer Name', dataIndex: 'customer_name', key: 'customer_name' },
                { title: 'Date', dataIndex: 'created_at', key: 'created_at' },
                { title: 'Qty Sold', dataIndex: 'quantity', key: 'quantity', align: 'center', render: q => <Tag color="blue">{q}</Tag> },
              ]}
              dataSource={productSales}
              size="middle"
            />
          </Modal>
    </div>
  );
};

export default Product;


